import * as vp from 'vopay';
import {
  Box,
  Pagination,
  GetColorStatus,
  capitalizeFirstLetter,
  capitalizeFirstLetterOfWords,
  formattedCurrency,
  usePagination,
  useUpdateQueryParams,
} from 'vopay';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { FunctionComponent, useState, useEffect, useMemo } from 'react';
import type { DefaultOptionProps } from 'vopay';
import { format, parseISO } from 'date-fns';
import useDataFetchIndicator from '@/hooks/useDataFetchIndicator';
import ScheduledTransactionsForm from '@/components/Transaction/ScheduledTransactions/ScheduledTransactionsForm';
import useFetchScheduledTransactions from '@/hooks/useApi/useFetchScheduledTransactions';
import { useClientData } from '@/providers/ClientData.provider';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import { useI18nLabels } from '@/utils/useI18nLabels';
import ActivityType from '../ActivityType';
import type { ActivityTabType } from '../ActivityTab';
import { TableFiltersContainer, TransactionStatus, Table } from 'UI';
import styles from '../ActivityTab.module.scss';

type QueryParams = {
  status?: string;
  scheduleType?: string;
  transactionType?: string;
  frequency?: vp.NameOfFrequency | 'all';
  searchTerm?: string;
  startDate?: string;
  endDate?: string;
  activityType?: ActivityTabType;
};

type TransactionParams = {
  status: string;
  searchTerm: string;
  startDate: string;
  endDate: string;
  frequency: vp.NameOfFrequency | 'all';
  scheduleType: string;
  transactionType: string;
  hasInvalidEndDate: boolean;
  offset?: number;
};

type ScheduledTransactionHistoryTableProps = {
  accountID: string;
  activityTypeOptions: DefaultOptionProps[];
  currentType: string;
  handleCurrentType: (val: ActivityTabType) => void;
};

const ScheduledTransactionHistoryTable: FunctionComponent<ScheduledTransactionHistoryTableProps> = ({
  accountID,
  activityTypeOptions,
  currentType,
  handleCurrentType,
}) => {
  const alertDialog = useAlertDialog();
  const { response } = useClientData();
  const { t } = useTranslation('translations', { keyPrefix: 'ScheduledTransactionsHistory' });
  const { frequencyLabelMapper } = useI18nLabels();
  const accountIDType = response.clientAccountsEnabled ? 'ClientAccountID' : 'ContactID';
  const [, setDataFetchingInfo] = useDataFetchIndicator();
  const [hasInvalidEndDate, setHasInvalidEndDate] = useState(false);
  const [shouldShowEndDateError, setShouldShowEndDateError] = useState(false);
  const [filteredList, setFilteredList] = useState<vp.ScheduledTransactionsList[]>([]);
  const [scheduledTransactionsResponse, , , fetchScheduledTransactions] = useFetchScheduledTransactions({});

  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const initialStartDate = moment().subtract(7, 'day').format('YYYY-MM-DD');
  const initialEndDate = moment().format('YYYY-MM-DD');
  const status = useMemo(() => queryParams.get('status') || '', [queryParams]);
  const searchTerm = useMemo(() => queryParams.get('searchTerm') || '', [queryParams]);
  const startDate = useMemo(() => queryParams.get('startDate') || initialStartDate || '', [queryParams]);
  const endDate = useMemo(() => queryParams.get('endDate') || initialEndDate || '', [queryParams]);
  const frequency = useMemo(
    () => (queryParams.get('frequency') as vp.NameOfFrequency | 'all') || '',
    [queryParams],
  );
  const scheduleType = useMemo(() => queryParams.get('scheduleType') || '', [queryParams]);
  const transactionType = useMemo(() => queryParams.get('transactionType') || '', [queryParams]);
  const filtersApplied = useMemo(() => {
    return Boolean(
      status ||
        searchTerm ||
        frequency ||
        transactionType ||
        scheduleType ||
        (startDate && startDate !== initialStartDate) ||
        (endDate && endDate !== initialEndDate),
    );
  }, [
    status,
    searchTerm,
    frequency,
    transactionType,
    scheduleType,
    startDate,
    endDate,
    initialStartDate,
    initialEndDate,
  ]);

  const updateQueryParams = useUpdateQueryParams<QueryParams>();

  // pagination
  const totalDataLength = useMemo(() => {
    return scheduledTransactionsResponse.PaginationMetaData?.TotalRecords || 0;
  }, [scheduledTransactionsResponse.ScheduledTransactions]);

  const PER_PAGE = 20;
  const { pageCount, pageIndex, setPageIndex } = usePagination(totalDataLength, {
    pageSize: PER_PAGE,
  });

  const fetchScheduledTransactionHistory = async ({
    startDate,
    endDate,
    status,
    hasInvalidEndDate,
    searchTerm,
    frequency,
    scheduleType,
    transactionType,
    offset = 0,
  }: TransactionParams) => {
    const scheduledTransactionHistoryRequest = {
      StartDate: startDate,
      EndDate: endDate,
      SearchText: searchTerm,
      Frequency: scheduleType,
      ScheduleTransactionType: transactionType,
      NameOfFrequency: frequency !== 'all' ? frequency : undefined,
      ScheduledTransactionStatus: status,
      ScheduledTransactionID: '',
      Limit: PER_PAGE,
      Offset: offset,
      [accountIDType]: accountID,
    };

    if (hasInvalidEndDate) {
      setShouldShowEndDateError(true);
      return;
    }

    try {
      const res = await fetchScheduledTransactions(scheduledTransactionHistoryRequest);

      if (!res.Success) throw new Error(res.ErrorMessage);
      const scheduledTransactionRequest = Object.values(res.ScheduledTransactions || {});
      setFilteredList(scheduledTransactionRequest);
    } catch (err) {
      const msg = err instanceof Error ? err.message : t('ErrorMessages.fetchTransactions');
      alertDialog.error(t('ErrorMessages.error'), msg);
      console.error(err);
    }
    setDataFetchingInfo(false);
  };

  useEffect(() => {
    const offset = setPageIndex(0);
    fetchScheduledTransactionHistory({
      startDate,
      endDate,
      status,
      hasInvalidEndDate: false,
      searchTerm,
      frequency,
      scheduleType,
      transactionType,
      offset,
    });
    updateQueryParams({
      activityType: 'Scheduled Transactions',
    });
  }, []);

  useEffect(() => {
    setHasInvalidEndDate(startDate > endDate);
  }, [startDate, endDate]);

  const tableProps: vp.TableProps<vp.ScheduledTransactionsList> = {
    id: 'sheduled-transactions-list',
    version: '2',
    zebra: true,
    columns: [
      {
        id: 'id',
        header: t('Columns.id'),
        align: 'left',
        template: ({ data }) => {
          return (
            <Table.Link
              to={{
                pathname: `/scheduled-transactions/details/${data.ID}`,
                state: {
                  transactionId: data.ID,
                },
              }}
            >
              {data.ID}
            </Table.Link>
          );
        },
      },
      {
        id: 'name',
        header: t('Columns.name'),
        align: 'left',
        template: ({ data }) =>
          data.ScheduledDetails.CompanyName ? (
            <>{data.ScheduledDetails.CompanyName}</>
          ) : (
            <>
              {data.ScheduledDetails.FirstName} {data.ScheduledDetails.LastName}
            </>
          ),
      },
      { id: 'scheduleType', header: t('Columns.scheduleType'), align: 'left', field: 'Frequency' },
      { id: 'transactionType', header: t('Columns.transactionType'), align: 'left', field: 'Type' },
      {
        id: 'frequency',
        header: t('Columns.frequency'),
        align: 'left',
        template: ({ data }) =>
          data.NameOfFrequency
            ? frequencyLabelMapper(data.NameOfFrequency) || capitalizeFirstLetterOfWords(data.NameOfFrequency)
            : '',
      },
      {
        id: 'amount',
        header: t('Columns.amount'),
        align: 'left',
        template: ({ data }) =>
          (
            <>
              ${formattedCurrency(data.Amount)} {response.Country === 'CA' ? 'CAD' : 'USD'}
            </>
          ) || '',
      },
      { id: 'startDate', header: t('Columns.startDate'), align: 'left', field: 'StartDate' },
      { id: 'ending', header: t('Columns.ending'), align: 'left', field: 'EndDate' },
      {
        id: 'account',
        header: t('Columns.account'),
        align: 'left',
        template: ({ data }) => data.ScheduledDetails.AccountNumber,
      },
      {
        id: 'status',
        header: t('Columns.status'),
        align: 'left',
        template: ({ data }) => (
          <Box flx="row" ai="center">
            <TransactionStatus status={GetColorStatus(data.Status)} label={capitalizeFirstLetter(data.Status)} />
          </Box>
        ),
      },
    ],
    rows: filteredList.map((item): { id: string; data: vp.ScheduledTransactionsList } => ({
      id: String(item.ID || ''),
      data: item,
    })),
  };

  return (
    <Box flx="col">
      <TableFiltersContainer filtersApplied={filtersApplied}>
        <Box jc="sb" ai="center">
          <ActivityType
            activityTypeOptions={activityTypeOptions}
            currentType={currentType}
            handleCurrentType={handleCurrentType}
          />
          <TableFiltersContainer.Trigger />
        </Box>
        <TableFiltersContainer.Content>
          <ScheduledTransactionsForm
            timeRange={[moment(startDate).valueOf() as vp.Timestamp, moment(endDate).valueOf() as vp.Timestamp]}
            scheduleType={scheduleType}
            frequency={frequency}
            status={status}
            searchTerm={searchTerm}
            transactionType={transactionType}
            hasInvalidEndDate={hasInvalidEndDate}
            shouldShowEndDateError={shouldShowEndDateError}
            setShouldShowEndDateError={setShouldShowEndDateError}
            initialTimeRange={[
              parseISO(startDate).getTime() as vp.Timestamp,
              parseISO(endDate).getTime() as vp.Timestamp,
            ]}
            search={(searchParams) => {
              updateQueryParams({
                status: searchParams.status,
                searchTerm: searchParams.searchTerm,
                startDate: format(new Date(searchParams.timeRange[0]), 'yyyy-MM-dd'),
                endDate: format(new Date(searchParams.timeRange[1]), 'yyyy-MM-dd'),
                scheduleType: searchParams.scheduleType,
                transactionType: searchParams.transactionType,
                frequency: searchParams.frequency,
              });

              fetchScheduledTransactionHistory({
                status: searchParams.status,
                searchTerm: searchParams.searchTerm,
                startDate: format(new Date(searchParams.timeRange[0]), 'yyyy-MM-dd'),
                endDate: format(new Date(searchParams.timeRange[1]), 'yyyy-MM-dd'),
                scheduleType: searchParams.scheduleType,
                transactionType: searchParams.transactionType,
                frequency: searchParams.frequency,
                hasInvalidEndDate: searchParams.timeRange[0] > searchParams.timeRange[1],
              });
            }}
          />
        </TableFiltersContainer.Content>
      </TableFiltersContainer>

      <div className={styles.tableContainer}>
        <Table {...tableProps} />
      </div>
      <Box width="100%" jc="center" mt="lg" mb="sm">
        {pageCount > 1 && (
          <Pagination
            pageCount={pageCount}
            forcePageNumber={pageIndex}
            handlePageClick={(e) => {
              const offset = setPageIndex(e.selected);
              fetchScheduledTransactionHistory({
                startDate,
                endDate,
                status,
                hasInvalidEndDate: startDate > endDate,
                searchTerm,
                frequency,
                scheduleType,
                transactionType,
                offset,
              });
            }}
          />
        )}
      </Box>

      {tableProps.rows && tableProps.rows.length === 0 && (
        <Box flx="col" jc="center">
          <Box ai="center" jc="center" pt="md">
            {t('noRecordMessage')}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default ScheduledTransactionHistoryTable;
